import type { IDuplicatesState } from 'reactApp/modules/duplicates/duplicates.types';
import type { RootState } from 'reactApp/store';
import { createSelector } from 'reselect';

const getDuplicatesState = (state: RootState): IDuplicatesState => state.duplicates;

export const getDuplicatesPreviewItems = (state: RootState) => getDuplicatesState(state).preview.list;

export const getDuplicatesPreviewItemsLoadingState = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return duplicatesState.preview.isLoading;
    }
);

export const getDuplicatesPreviewItemsErrorState = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return duplicatesState.preview.isError;
    }
);

export const getDuplicatesItems = (state: RootState) => getDuplicatesState(state).items;

export const getDuplicatesList = (state: RootState) => getDuplicatesState(state).items.list;

export const getDuplicatesListLoadedState = (state: RootState) => getDuplicatesState(state).items.isLoaded;

export const getDuplicatesListErrorState = (state: RootState) => getDuplicatesState(state).items.error;

export const getDuplicatesListIds = (state: RootState) => getDuplicatesState(state).items.listIds;

export const getDuplicatesGroupsList = (state: RootState) => getDuplicatesState(state).groups.list;

export const getDuplicatesLoadedGroupsCount = (state: RootState) => Object.keys(getDuplicatesState(state).groups.list).length;

export const getDuplicatesGroupsCount = (state: RootState) => getDuplicatesState(state).count.count;

export const getDuplicatesGroupsTotal = (state: RootState) => getDuplicatesState(state).count.total;

export const getDuplicatesCursor = (state: RootState) => getDuplicatesState(state).items.cursor;

export const getDuplicatesGroupsCountLoadingState = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return duplicatesState.count.isLoading;
    }
);

export const getDuplicatesGroupsCountErrorState = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return duplicatesState.count.isError;
    }
);

export const getUserHasDuplicates = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return duplicatesState.count.count > 0;
    }
);

export const getDuplicatesItemById = createSelector(
    (_: RootState, id: string) => id,
    getDuplicatesList,
    (id, list) => {
        return list[id];
    }
);

export const getDuplicatesGroupById = createSelector(
    (_: RootState, id: string) => id,
    getDuplicatesGroupsList,
    (id, list) => {
        return list[id];
    }
);

export const getFirstDuplicatesBestPhotoId = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return duplicatesState.items.listIds[0];
    }
);

export const getShowBestPhotoTooltip = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return duplicatesState.showBestPhotoTooltip;
    }
);

export const getDuplicatesListCount = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return duplicatesState.items.listIds.length;
    }
);

export const getDuplicatesListSize = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return Object.values(duplicatesState.items.list).reduce((accumulator, item) => accumulator + item.size, 0);
    }
);

export const getDuplicatesDeletionComplete = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (duplicatesState) => {
        return duplicatesState.deletionComplete;
    }
);

export const getDuplicateGroup = createSelector(
    (state: RootState) => getDuplicatesState(state),
    (_, id: string) => id,
    (duplicatesState, id) => {
        return duplicatesState.groups.list[id];
    }
);
